'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { FileText, PlusCircle, ChevronLeft, ChevronRight, Leaf } from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  {
    id: 'nav-quotations',
    label: 'Quotations',
    href: '/',
    icon: <FileText size={20} />,
  },
  {
    id: 'nav-new-quote',
    label: 'New Quotation',
    href: '/quotation-form',
    icon: <PlusCircle size={20} />,
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  return (
    <aside
      className={`relative flex flex-col h-full bg-card border-r border-border transition-all duration-300 ease-in-out ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-border ${collapsed ? 'justify-center px-2' : ''}`}>
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary flex items-center justify-center overflow-hidden">
          <AppLogo size={36} className="rounded-lg" />
        </div>
        {!collapsed && (
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-bold text-foreground leading-tight truncate">TGIQuote</span>
            <span className="text-xs text-muted-foreground truncate">Terrace Garden India</span>
          </div>
        )}
      </div>

      {/* Nav Items */}
      <nav className="flex-1 px-2 py-4 space-y-1">
        {!collapsed && (
          <p className="px-2 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Menu</p>
        )}
        {navItems.map((item) => {
          const isActive = pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href));
          return (
            <Link
              key={item.id}
              href={item.href}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group relative ${
                isActive
                  ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
              } ${collapsed ? 'justify-center' : ''}`}
            >
              <span className={`flex-shrink-0 ${isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}`}>
                {item.icon}
              </span>
              {!collapsed && <span className="truncate">{item.label}</span>}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-primary-foreground text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                  {item.label}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className={`px-2 py-4 border-t border-border ${collapsed ? 'flex justify-center' : ''}`}>
        {!collapsed && (
          <div className="px-3 py-3 rounded-lg bg-secondary/50 mb-3">
            <div className="flex items-center gap-2 mb-1">
              <Leaf size={14} className="text-primary" />
              <span className="text-xs font-semibold text-primary">ISO 9001:2015</span>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">Certified quality. 25+ years of urban landscaping.</p>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : <><ChevronLeft size={16} /><span>Collapse</span></>}
        </button>
      </div>
    </aside>
  );
}